/**
 * Lambda-backed Toast data fetch for R0002.
 *
 * Replaces getToastData's browser-side pagination. Both reporting periods are
 * fetched in ONE GraphQL call; the resolver pages DynamoDB in-region and returns
 * the same two-page structure transformToastData2 produced.
 *
 * @module clients/grm/views/R0002/ToastData/getToastDataV2
 */

import { API } from 'aws-amplify'
import { getR0002ToastData } from './r0002ToastQueries'

/**
 * The only storeMap fields the resolver consumes.
 *
 * Deliberately minimal: storeRecordId to join, shortName/area/status/type to
 * classify, toastGoLiveDate to cut Toast rows against Aloha rows.
 */
const STORE_FIELDS = ['storeRecordId', 'shortName', 'area', 'status', 'type', 'toastGoLiveDate']

/**
 * Serialize the client's storeMap into the resolver's input shape.
 *
 * ORDER IS SIGNIFICANT. The browser resolves a storeId by iterating storeMap and
 * breaking on the first match, so a duplicated storeRecordId resolves to the
 * FIRST entry. The resolver reproduces that, but only if we preserve Map
 * insertion order here. Do not sort.
 *
 * Entries without a storeRecordId are dropped: useAreas sets it null when no
 * StoreRecord matches by toastId, and a null id can never match a
 * MetricData.storeId. Removing them cannot change which entry is first for any
 * key, so this is equivalent to the browser's behaviour.
 *
 * @param {Map<string, Object>} storeMap - From hooks/clients/grm/useAreas.
 * @returns {Array<Object>} Ordered store entries.
 */
export function serializeStoreMap(storeMap) {
  if (!storeMap || typeof storeMap.forEach !== 'function') return []

  const stores = []
  storeMap.forEach((store) => {
    if (!store || !store.storeRecordId) return
    const entry = {}
    for (const field of STORE_FIELDS) {
      entry[field] = store[field] === undefined ? null : store[field]
    }
    stores.push(entry)
  })
  return stores
}

/**
 * Decode one AWSJSON page string returned by the resolver.
 *
 * AppSync serialises whatever the Lambda returns for an AWSJSON field, so the
 * encoding depth depends on the resolver. It originally returned an
 * already-stringified array, which AppSync encoded a second time — one parse
 * then yields the inner JSON string, not the array. That is the same artifact
 * getReportv2 exhibits (its consumers all `JSON.parse(JSON.parse(...))`), and it
 * was assumed away rather than measured. index.js now returns the array itself,
 * which AppSync encodes once.
 *
 * Parsing until the result stops being a string tolerates either encoding, so
 * this is correct against both the fixed resolver and any still-deployed build
 * of the old one — client and backend never have to ship in lockstep.
 *
 * @param {string} raw - The AWSJSON field value.
 * @param {string} field - Field name, for the error message.
 * @returns {Array} The decoded page array.
 * @throws {Error} If the value never decodes to an array.
 */
function decodePages(raw, field) {
  let value = raw
  // Two unwraps covers single- and double-encoding. A third would mean the
  // payload is not what this contract describes, so fail rather than keep going.
  for (let i = 0; i < 2 && typeof value === 'string'; i += 1) {
    value = JSON.parse(value)
  }

  if (!Array.isArray(value)) {
    throw new Error(
      `getR0002ToastData.${field} did not decode to a page array (got ${typeof value})`
    )
  }
  return value
}

/**
 * Fetch aggregated Toast data for both reporting periods.
 *
 * @param {string} curSDate - Current period start, 'yyyy-MM-dd'.
 * @param {string} curEDate - Current period end, 'yyyy-MM-dd'.
 * @param {string} compSDate - Comparison period start, 'yyyy-MM-dd'.
 * @param {string} compEDate - Comparison period end, 'yyyy-MM-dd'.
 * @param {Map<string, Object>} storeMap - From useAreas.
 * @param {string} shiftString - Subset of 'L', 'S', 'D'.
 * @returns {Promise<{toastCur: Array, toastComp: Array, meta: Object}>}
 * @throws {Error} If the query fails or returns an unusable payload.
 */
export default async function getToastDataV2(
  curSDate, curEDate, compSDate, compEDate, storeMap, shiftString
) {
  const stores = serializeStoreMap(storeMap)

  const response = await API.graphql({
    query: getR0002ToastData,
    variables: {
      input: {
        // Preserves the literal hardcoded at fetchToastData.js:72. The grm route
        // is the only mount of this component; dem uses its own R0002Main with
        // no Toast path.
        clientId: 'grm',
        curStartDate: curSDate,
        curEndDate: curEDate,
        compStartDate: compSDate,
        compEndDate: compEDate,
        shiftString,
        stores
      }
    },
    authMode: 'AMAZON_COGNITO_USER_POOLS'
  })

  const payload = response?.data?.getR0002ToastData
  if (!payload) {
    throw new Error('getR0002ToastData returned no payload')
  }

  const toastCur = decodePages(payload.current, 'current')
  const toastComp = decodePages(payload.comparison, 'comparison')

  return { toastCur, toastComp, meta: payload.meta }
}
